import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { motion, AnimatePresence } from 'framer-motion';
import { logDelsApi } from '../../services/LogisticFeeService';
import { fetchTrackingStatuses } from '../../api/trackingApi';
import { Link } from 'react-router-dom';
import MetaTags from 'react-meta-tags';
import { Card, CardBody, Col, Container, Row, FormGroup, Label } from 'reactstrap';
import { AvField } from 'availity-reactstrap-validation';
import { Flower2, Leaf, Snowflake, Sun, Atom } from 'lucide-react';
import NumberFormat from 'react-number-format';
import Breadcrumbs from '../../components/Breadcrumb';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
// import { Type } from 'react-bootstrap-table2-editor';
// import { textFilter, selectFilter, dateFilter } from 'react-bootstrap-table2-filter';
import filterFactory, { textFilter, selectFilter, dateFilter } from 'react-bootstrap-table2-filter';
import cellEditFactory, { Type } from 'react-bootstrap-table2-editor';
import InputMask from 'react-input-mask';
import { Dropdown } from 'semantic-ui-react';
import { countryOptions } from '../../helpers/country_helper';
import { showToast } from '../../components/Toast';
import TrackingColumnPrTest from '../../components/TrackingColumnPrTest';
import PageLoader from '../../components/Loaders/newPageLoader';
import useLoadingProgress from '../../hooks/useLoadingProgress';
import SweetAlert from 'react-bootstrap-sweetalert';
import { LIVE_LINK } from '../../helpers/url_helper';
import { MainContent } from '../../components/Table/MainContent';
import { BtnModal } from '../../components/Table/BtnModal';

export default function LogFee({ profileData }) {
  const defaultValues = {
    startDate: '',
    endDate: '',
    clientName: '',
    years: [new Date().getFullYear()],
    months: [new Date().getMonth()]
    // months: [3, 4, 5]
  };
  const {
    data: baseData,
    isFetching: isLoadingBase,
    error,
    refetch,
  } = logDelsApi.useFetchAllLogDeliveriesQuery(defaultValues, {
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
  });
  const [getFilteredLogisticDel, { data: filteredData, isFetching: isLoadingFiltered }] = logDelsApi.useLazyFilterLogDeliveriesQuery();
  const [addLogisticDel, { isLoading: isSubmitting }] = logDelsApi.useAddLogDeliveryMutation();
  const [updateLogisticDel, { isLoading: isUpdating }] = logDelsApi.useUpdateLogDeliveryMutation();
  const [deleteLogisticDel, { isLoading: isDeleting }] = logDelsApi.useDeleteLogDeliveryMutation();

  const [view, setView] = useState('base');
  const [filterFormValues, setFilterFormValues] = useState(defaultValues);
  // const [useFilteredData, setUseFilteredData] = useState(true);
  const [filterValues, setFilterValues] = useState({});
  
  const [tabDataMap, setTabDataMap] = useState({});
  const [managers, setManagersData] = useState([]);

  const [visitedTabs, setVisitedTabs] = useState(new Set([1]));
  const [activeTab, setActiveTab] = useState(1);
  const activeTabRef = useRef(activeTab);

  const loading = view === 'filtered' ? isLoadingFiltered : isLoadingBase;

  const metaSource = useMemo(() => {
    return baseData || null;
  }, [baseData]);

  const mergedData = useMemo(() => {
    if (!metaSource) return null;

    if (view === 'filtered' && filteredData) {
      return {
        ...metaSource,
        delDHL: filteredData.delDHL ?? [],
        delFedEx: filteredData.delFedEx ?? [],
        delEMS: filteredData.delEMS ?? [],
        delEMSMC: filteredData.delEMSMC ?? [],
        delEMSMN: filteredData.delEMSMN ?? [],
        delOthers: filteredData.delOthers ?? []
      };
    }

    return metaSource;
  }, [view, filteredData, metaSource]);

  useEffect(() => {
    if (!mergedData || loading) return;

    const newTabDataMap = {
      1: mergedData.delDHL?.length ? [...mergedData.delDHL] : [],
      2: mergedData.delFedEx?.length ? [...mergedData.delFedEx] : [],
      3: mergedData.delEMS?.length ? [...mergedData.delEMS] : [],
      4: mergedData.delEMSMC?.length ? [...mergedData.delEMSMC] : [],
      5: mergedData.delEMSMN?.length ? [...mergedData.delEMSMN] : [],
      6: mergedData.delOthers?.length ? [...mergedData.delOthers] : []
    };

    setTabDataMap(newTabDataMap);

    if (mergedData.managers?.length) {
      setManagersData([...mergedData.managers]);
    }
  }, [mergedData, loading]);

  useEffect(() => {
    setVisitedTabs((prev) => new Set(prev).add(activeTab));
    activeTabRef.current = activeTab;
  }, [activeTab]);

  useEffect(() => {
    const evtSource = new EventSource(`${LIVE_LINK}/logistics/stream`);

    evtSource.onmessage = (event) => {
      const { data } = JSON.parse(event.data);

      setTabDataMap((prev) => {
        const currentActiveTab = activeTabRef.current;
        return {
          ...prev,
          [currentActiveTab]: prev[currentActiveTab].map((row) => {
            if (Array.isArray(row.delInfo?.trackingNo) && row.delInfo?.trackingNo.includes(data.number)) {
              return {
                ...row,
                delInfo: {
                  ...row.delInfo,
                  trackingInfo: {
                    ...row.trackingInfo,
                    [data.number]: {
                      status: data.status,
                      countryFrom: data.countryFrom,
                      countryTo: data.countryTo
                    }
                  }
                }
              };
            }

            return row;
          })
        };
      });
    };

    evtSource.onerror = (err) => {
      console.error('❌ SSE Error:', err);
      evtSource.close(); // Optionally close on error
    };

    return () => {
      evtSource.close(); // Clean up when page unmounts
    };
  }, []);
  const { progress, step, visible } = useLoadingProgress({
    isLoading: loading,
    isFetching: loading,
    // isSubmitting: isSubmitting,
    isLoadingFiltered
  });
console.log('filterFormValues ', filterFormValues)
  /**
   * Parses a date-time string in the format "DD/MM/YYYY, HH:MM:SS" to an ISO 8601 string.
   * 
   * @param {string} str - The date-time string to parse, expected in "DD/MM/YYYY, HH:MM:SS" format.
   * @returns {string|null} The ISO 8601 formatted date string if parsing is successful, otherwise null.
   */
  const parseToISO = (str) => {
    const match = str.match(/^(\d{2})\/(\d{2})\/(\d{4}), (\d{2}):(\d{2}):(\d{2})$/);
    if (!match) return null;

    const [, day, month, year, hour, minute, second] = match;
    const date = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}Z`);
    return isNaN(date) ? null : date.toISOString();
  };
  /**
   * Handles saving delivery information for a logistic record, performing necessary value transformations,
   * updating nested fields immutably, and triggering backend updates. Special handling is applied for certain
   * fields (e.g., sendDate, country, trackingNo). Also updates filter form values and timestamps, and fetches
   * tracking statuses if needed. Displays toast notifications on success or failure.
   *
   * @async
   * @param {*} oldValue - The previous value of the edited field.
   * @param {*} newValue - The new value to be saved.
   * @param {Object} row - The original row data object.
   * @param {Object} column - The column definition object, containing dataField and other metadata.
   * @param {string|number} rowId - The unique identifier for the row.
   * @param {string|number} tabId - The identifier for the current tab or context.
   * @param {Array} columns - The array of all column definitions.
   * @returns {Promise<void>} Resolves when the update is complete.
   */
  const handleSaveDelInfo = async (oldValue, newValue, row, column, rowId, tabId, columns) => {
    const fieldPath = column.dataField.split('.');
    const originalValue = fieldPath.reduce((acc, key) => acc?.[key], row);
    const isSpecialField = ['sendDate', 'country'].includes(column.dataField);
    let finalValue = newValue;
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/;
    if (
      (originalValue === null || (typeof originalValue === 'string' && isoDateRegex.test(originalValue))) &&
      typeof newValue === 'string' &&
      !isoDateRegex.test(newValue)
    ) {
      const parsedISO = parseToISO(newValue);
      if (parsedISO) {
        finalValue = parsedISO;
      }
    }
    try {
      // Now apply the update immutably
      const updatedRow = { ...row, defaultValues };
      if (isSpecialField) {
        const targetKey = row.delServiceCommon === 'Others' ? 'logInfo' : 'delInfo';
        updatedRow[targetKey] = {
          ...updatedRow[targetKey],
          [column.dataField]: finalValue,
        };
        updatedRow[column.dataField] = finalValue;
      } else if (fieldPath.length === 1) {
        updatedRow[column.dataField] = finalValue;
      } else {
        let current = updatedRow;
        for (let i = 0; i < fieldPath.length - 1; i++) {
          current[fieldPath[i]] = { ...current[fieldPath[i]] };
          current = current[fieldPath[i]];
        }
        current[fieldPath.at(-1)] = finalValue;
      }
      
      // Add filterFormValues to updatedRow
      // updatedRow.filterFormValues = {
      //   ...Object.fromEntries(Object.entries({ ...filterFormValues }).filter(([key]) => key in defaultValues)),
      //   years: filterFormValues.years.map(Number),
      //   months: filterFormValues.months.map(Number)
      // };
      updatedRow.filterFormValues = view === 'filtered' ? {
        ...Object.fromEntries(
          Object.entries(filterFormValues).filter(([k]) => k in defaultValues)
        ),
        years: filterFormValues.years.map(Number),
        months: filterFormValues.months.map(Number),
      } : null;

      let trackings = [];
      if (column.dataField === 'delInfo.trackingNo' && updatedRow.delInfo?.trackingNo) {
        trackings =
          typeof newValue === 'string'
            ? newValue
                .split(/[, .\n]+/)
                .map((num) => num.trim())
                .filter((num) => /\d/.test(num))
            : Array.isArray(newValue)
            ? newValue
            : [];
        updatedRow.delInfo.trackingNo = trackings;
      }
      // Update the logistics record
      await updateLogisticDel(updatedRow).unwrap();

      if (column.dataField === 'delInfo.trackingNo' && updatedRow.delInfo?.trackingNo) {
        await fetchTrackingStatuses(trackings, 'register');
      }
      showToast('Row is updated');
    } catch (error) {
      console.error('Failed to update row:', error);
      showToast('Failed to update row', 'error');
    }
  }
  /**
   * Handles the submission of a global action on selected logistic rows.
   * 
   * This function processes an array of selected rows and applies the specified action
   * (such as updating send date or delivery paid date) with the provided values.
   * It deep clones each row to avoid direct mutation, updates relevant fields based on the action,
   * attaches filtered form values, and triggers an asynchronous update for each row.
   * Displays an error toast if any update fails.
   *
   * @async
   * @param {Array<Object>} selectedRows - The array of selected row objects to update.
   * @param {Object} values - The action values to apply.
   * @param {string} values.action - The action type to perform (e.g., 'sendDate', 'delPaidDate').
   * @param {string|Date} values.date - The date value to set for the action.
   * @returns {Promise<void>} Resolves when all updates are complete.
   */
  const handleGlobalActionSubmit = async (selectedRows, values) => {
    let updatedRows = [];
    selectedRows.map((row) => {
      updatedRows.push({
        row: row,
        action: values.action,
        date: values.date
      });
    });
    try {
      // setIsUpdating(true);
      await Promise.all(
        updatedRows.map(({ row, action, date }) => {
          // Deep clone to avoid mutation
          const updatedRow = JSON.parse(JSON.stringify(row));
          switch (action) {
            case 'sendDate':
              updatedRow.sendDate = date;
              updatedRow.delInfo.sendDate = date;
              break;
            case 'delPaidDate':
              updatedRow.delInfo.delPaidDate = date;
              break;
            default:
              break;
          }
          // Add filterFormValues
          updatedRow.filterFormValues = {
            ...Object.fromEntries(Object.entries({ ...filterFormValues }).filter(([key]) => key in defaultValues)),
            years: filterFormValues.years.map(Number),
            months: filterFormValues.months.map(Number)
          };
          return updateLogisticDel(updatedRow).unwrap();
        })
      );
    } catch (error) {
      showToast('Failed to update row', 'error');
    }
  };
  /**
   * Handles the submission of the filter form by processing and formatting filter values,
   * updating the component state, and fetching logistic delivery data based on the filters.
   *
   * @async
   * @param {Object} filters - The initial filter values.
   * @param {Object} values - The values submitted from the filter form.
   * @returns {Promise<void>} Resolves when the logistic data has been fetched and state updated.
   */
  const filterFormSubmit = async (filters, values) => {
    let filterValues = { 
      ...filters,
      startDate: values['startDate'] || '', 
      endDate: values['endDate'] || '',
      clientName: values['clientName'] || ''
    };
    filterValues.years = filterValues.years.map(Number);
    filterValues.months = filterValues.months.map(Number);
    setFilterFormValues(filterValues)
    setView('filtered');
    try {
      await getFilteredLogisticDel(filterValues).unwrap();
    } catch (error) {
      console.error('Failed to submit request', error.message);
      showToast('Failed to submit request', 'error');
    }
  }
  /**
   * Handles the submission of new delivery entries, processes form values, 
   * constructs delivery objects based on the selected delivery service, 
   * and submits them to the backend. Also fetches tracking statuses for 
   * new deliveries and manages UI feedback such as modals and toast notifications.
   *
   * @async
   * @function handleNewDeliverySubmit
   * @param {Event} e - The form submission event.
   * @param {Object} values - The form values for the new delivery.
   * @returns {Promise<void>} Resolves when the submission and related actions are complete.
   */
  const handleNewDeliverySubmit = async (e, values) => {
    // setIsSubmitting(true);
    let newDels = [];
    addDelsFields.forEach((del) => {
      if (delServ === 'Others') {
        // Push a new input field state
        newDels.push({
          // Logistic fields
          logService: del.logService,
          logName: del.logName,
          logDocs: del.logDocs,
          sendDate: sendDate.split('/').reverse().join('-'),
          depDate: depDate.split('/').reverse().join('-'),
          delCost: isNaN(parseInt(del.delCost)) ? 0 : parseInt(del.delCost),
          delCostVAT: isNaN(parseInt(del.delCostVAT)) ? 0 : parseInt(del.delCostVAT),
          delivery: del.delivery,
          oneCOrder: delivery ? delivery.invoiceNo : del.oneCOrder,
          clientName: delivery ? delivery.clientName : del.clientName,
          country: del.country,
          manager: delivery ? delivery.manager : del.manager,
          comment: del.comment,
          delPaidDate: delPaidDate.split('/').reverse().join('-'),
          logCost: isNaN(parseInt(del.logCost)) ? 0 : parseInt(del.logCost),
          logCostCurrency: del.logCostCurrency,
          year: del.year,
          month: del.month,
          table: 'Others',
          delService: 'Others',
          dataId: del.dataId,
          filterFormValues: {
            ...Object.fromEntries(Object.entries({ ...filterFormValues }).filter(([key]) => key in defaultValues)),
            years: filterFormValues.years.map(Number),
            months: filterFormValues.months.map(Number)
          }
        });
      } else {
        // Push a new input field state
        newDels.push({
          // Delivery fields
          sendDate: del.sendDate.split('/').reverse().join('-'),
          trackingNo: del.trackingNo,
          delFee: isNaN(parseInt(del.delFee)) ? 0 : parseInt(del.delFee),
          oneCOrder: delivery ? delivery.invoiceNo : del.oneCOrder,
          clientName: delivery ? delivery.clientName : del.clientName,
          country: del.country,
          delPaidDate: del.delPaidDate.split('/').reverse().join('-'),
          delInvoiceNo: del.delInvoiceNo,
          comment: del.comment,
          delivered: false,
          delService: delServ,
          year: del.year,
          month: del.month,
          table: 'NotOthers',
          dataId: del.dataId,
          defaultValues: defaultValues,
          filterFormValues: {
            ...Object.fromEntries(Object.entries({ ...filterFormValues }).filter(([key]) => key in defaultValues)),
            years: filterFormValues.years.map(Number),
            months: filterFormValues.months.map(Number)
          }
        });
      }
    });
    let trackings = [...new Set(newDels.map((del) => del.trackingNo))];
    try {
      await addLogisticDel(newDels).unwrap();
      trackings.length > 0 && (await fetchTrackingStatuses(trackings, 'register'));
      toggleAddModal();
      showToast('New delivery is added');
    } catch (error) {
      console.error('Failed to update row:', error);
      showToast('Failed to update row', 'error');
    }
  };
  /**
   * Handles the deletion of a delivery entry.
   * 
   * This asynchronous function manages the process of deleting a delivery record.
   * It resets relevant UI states, calls the API to perform the deletion, and provides
   * user feedback via toast notifications. If an error occurs during deletion, it logs
   * the error and notifies the user.
   *
   * @async
   * @function
   * @returns {Promise<void>} Resolves when the deletion process is complete.
   */
  const handleDeleteDelivery = async () => {
    try {
      // setIsDeleting(true);
      setRowToDelete(null);
      setShowDeleteAlert(false);
      await deleteLogisticDel({
        rowToDelete,
        filterFormValues: {
          ...Object.fromEntries(Object.entries({ ...filterFormValues }).filter(([key]) => key in defaultValues)),
          years: filterFormValues.years.map(Number),
          months: filterFormValues.months.map(Number)
        }
      });
      showToast('Delivery deleted successfully');
    } catch (error) {
      console.error('Failed to delete row:', error);
      showToast('Failed to delete row', 'error');
    }
  };
  const [selCountry, setSelCountry] = useState('');
  const [sendDate, setSendDate] = useState(new Date().toLocaleDateString('en-GB'));
  const [delPaidDate, setDelPaidDate] = useState(new Date().toLocaleDateString('en-GB'));
  const [depDate, setDepDate] = useState(new Date().toLocaleDateString('en-GB'));
  const [correctShipDate, setShipDateCorrecDate] = useState(true);
  const [correctDelPaidDate, setDelPaidDateCorrecDate] = useState(true);
  const [correctDepDate, setDepDateCorrecDate] = useState(true);
  const [delivery, setSelDelivery] = useState();
  const [addDelsFields, setAddInputFields] = useState([]);
  const [delServ, setDelService] = useState();
  // Set copy action
  const [isCopy, setIsCopy] = useState(false);
  // Set delivery service
  const setDeliveryService = (value) => {
    setDelService(value);
  };
  // Toggle modal
  const [modalAdd, setAddModal] = useState(false);
  const toggleAddModal = useCallback(() => {
    setSelDelivery('');
    setSelCountry('');
    setSendDate(new Date().toLocaleDateString('en-GB'));
    setDepDate(new Date().toLocaleDateString('en-GB'));
    setDelPaidDate(new Date().toLocaleDateString('en-GB'));
    setAddModal((prev) => !prev);
  }, []);

  /**
   * Resets the filter form values to their default state, including clearing date ranges,
   * client name, and resetting years and months to the current year and month.
   * Also resets filtered data usage and timestamp state.
   *
   * @async
   * @function
   * @returns {Promise<void>} Resolves when the form values and related states have been reset.
   */
  const filterFormReset = async () => {
    setFilterFormValues({
      startDate: '',
      endDate: '',
      clientName: '',
      years: [new Date().getFullYear()],
      months: [new Date().getMonth()]
    });
    // setTimeStamp(null);
    setView('base');

  }
  /**
   * Handles the event when the "New Delivery" button is clicked.
   * Resets the delivery service, prepares the order data for a new delivery,
   * and ensures the copy mode is disabled.
   */
  const handleNewDeliveryClick = () => {
    console.log('New Delivery Clicked');
    setDelService();
    prepareOrderData();
    setIsCopy(false);
  };
  /**
   * Handles the action of copying an order.
   * Sets the delivery service, prepares the order data for duplication,
   * and marks the state as a copy operation.
   *
   * @async
   * @param {Object} orderData - The data of the order to be copied.
   */
  const handleCopyOrderClick = async (orderData) => {
    setDelService(orderData.delService);
    prepareOrderData(orderData);
    setIsCopy(true);
  };
  /**
   * Prepares and sets order data for delivery or logistics forms based on the provided order data object.
   * Updates various state variables such as selected delivery, country, send date, and paid dates.
   * Depending on the delivery service type, constructs and sets additional input fields for either logistics or delivery information.
   * Finally, opens the modal for adding or editing order data.
   *
   * @param {Object} [orderData=''] - The order data object containing delivery or logistics information.
   */
  const prepareOrderData = (orderData = '') => {
    setSelDelivery(orderData);
    setSelCountry(orderData.country);
    orderData.sendDate && setSendDate(new Date(orderData.sendDate).toLocaleDateString('en-GB'));
    orderData.delInfo?.delPaidDate && setDepDate(new Date(orderData.delInfo.delPaidDate).toLocaleDateString('en-GB'));
    orderData.logInfo?.depDate && setDelPaidDate(new Date(orderData.logInfo.depDate).toLocaleDateString('en-GB'));
    const newAddFields = [];
    if (delServ === 'Others') {
      newAddFields.push({
        // Logistic fields
        logService: orderData.logInfo?.logService || '',
        logName: orderData.logInfo?.logName || '',
        logDocs: orderData.logInfo?.logDocs || '',
        sendDate: (orderData.sendDate && new Date(orderData.sendDate).toLocaleDateString('en-GB')) || sendDate,
        depDate: (orderData.logInfo?.depDate && new Date(orderData.logInfo?.depDate).toLocaleDateString('en-GB')) || depDate,
        delCost: orderData.logInfo?.delCost || '',
        delCostVAT: orderData.logInfo?.delCostVAT || '',
        delivery: orderData.logInfo?.delivery || '',
        oneCOrder: orderData.invoiceNo || '',
        clientName: orderData.clientName || '',
        manager: orderData.manager?._id || '',
        country: orderData.country || '',
        comment: orderData.logInfo?.comment || '',
        delPaidDate: (orderData.logInfo?.delPaidDate && new Date(orderData.logInfo?.delPaidDate).toLocaleDateString('en-GB')) || delPaidDate,
        logCost: orderData.logInfo?.logCost || '',
        logCostCurrency: orderData.logInfo?.logCostCurrency || '',
        dataId: activeTab,
        year: new Date(orderData.sendDate).getFullYear() || new Date().getFullYear(),
        month: new Date(orderData.sendDate).getMonth() || new Date().getMonth()
      });
    } else {
      newAddFields.push({
        // Delivery fields
        sendDate: (orderData.sendDate && new Date(orderData.sendDate).toLocaleDateString('en-GB')) || sendDate,
        trackingNo: orderData.delInfo?.trackingNo || [],
        delFee: orderData.delInfo?.delFee || '',
        oneCOrder: orderData.invoiceNo || '',
        clientName: orderData.clientName || '',
        country: orderData.country || '',
        delPaidDate: (orderData.delInfo?.delPaidDate && new Date(orderData.delInfo?.delPaidDate).toLocaleDateString('en-GB')) || delPaidDate,
        delInvoiceNo: orderData.delInfo?.delInvoiceNo || '',
        comment: orderData.delInfo?.comment || '',
        dataId: activeTab,
        year: new Date(orderData.sendDate).getFullYear() || new Date().getFullYear(),
        // month: new Date(orderData.sendDate).getMonth() || new Date().getMonth()
        month: ((d) => Number.isNaN(d.getTime()) ? new Date().getMonth() : d.getMonth())(new Date(orderData.sendDate))

      });
    }
    console.log(orderData)
    console.log(new Date(orderData.sendDate).getMonth())
    console.log(newAddFields)
    setAddInputFields(newAddFields);
    toggleAddModal();
  };
  /**
   * Adds a new input field object to the current state based on the selected delivery service.
   * 
   * If the selected delivery service (`delServ`) is 'Others', a new logistic input field object is added.
   * Otherwise, a new delivery input field object is added.
   * The function copies the existing input fields, appends a new field object with default values,
   * and updates the state with the new array.
   */
  const addInputField = () => {
    // Copy the current input fields state
    const newAddFields = [...addDelsFields];
    if (delServ === 'Others') {
      // Push a new input field state
      newAddFields.push({
        // Logistic fields
        logService: '',
        logName: '',
        logDocs: '',
        sendDate: sendDate,
        depDate: depDate,
        delCost: '',
        delCostVAT: '',
        delivery: '',
        oneCOrder: '',
        manager: '',
        country: '',
        comment: '',
        delPaidDate: delPaidDate,
        logCost: '',
        logCostCurrency: ''
      });
    } else {
      // Push a new input field state
      newAddFields.push({
        // Delivery fields
        sendDate: sendDate,
        trackingNo: [],
        comment: '',
        oneCOrder: delivery.invoiceNo,
        clientName: delivery.clientName,
        country: delivery.country,
        delPaidDate: delPaidDate,
        delInvoiceNo: '',
        delFee: ''
      });
    }
    // Set the new state
    setAddInputFields(newAddFields);
  };
  /**
   * Removes an input field from the addDelsFields array at the specified index and updates the state.
   *
   * @param {number} index - The index of the input field to remove.
   */
  const removeInputField = (index) => {
    // Copy the current input fields state
    const newAddFields = [...addDelsFields];
    // Remove the input field at the specified index
    newAddFields.splice(index, 1);
    // Update state with modified input fields
    setAddInputFields(newAddFields);
  };
  /**
   * Handles changes to input fields within a dynamic form, updating state accordingly.
   * Specifically validates date fields ('sendDate', 'delPaidDate', 'depDate') to ensure the month portion is valid (<= 12).
   * Updates corresponding correction state flags and values based on validation.
   * Updates the main input fields state with the new value.
   *
   * @param {number} index - The index of the field being updated in the addDelsFields array.
   * @param {string} field - The name of the field being updated.
   * @param {string} value - The new value entered by the user.
   */
  const handleInputChange = (index, field, value) => {
    const newAddFields = [...addDelsFields];
    if (field === 'sendDate' || field === 'delPaidDate' || field == 'depDate') {
      if (parseFloat(value.substring(3, 5)) > 12) {
        if (field === 'sendDate') {
          setShipDateCorrecDate(false);
        } else if (field === 'delPaidDate') {
          setDelPaidDateCorrecDate(false);
        } else if (field === 'depDate') {
          setDepDateCorrecDate(false);
        }
      } else {
        if (field === 'sendDate') {
          setShipDateCorrecDate(true);
          setSendDate(value);
        } else if (field === 'delPaidDate') {
          setDelPaidDateCorrecDate(true);
          setDelPaidDate(value);
        } else if (field === 'depDate') {
          setDepDateCorrecDate(true);
          setDepDate(value);
        }
      }
    }
    newAddFields[index][field] = value;
    setAddInputFields(newAddFields);
  };
  const inputRefs = useRef([]);
  /**
   * Handles the blur event for a specific input field in a dynamic form.
   * Updates the state of input fields by setting the value of the specified field
   * at the given index to the current value from the inputRefs.
   *
   * @param {number} index - The index of the input field in the array to update.
   * @param {string} field - The name of the field to update (e.g., 'name', 'value').
   */
  const handleBlur = (index, field) => {
    setAddInputFields((prevFields) => {
      const newFields = [...prevFields];
      newFields[index] = { ...newFields[index], [field]: inputRefs.current[index]?.[field] };
      return newFields;
    });
  };
  /**
   * Handles changes to input fields by updating the corresponding value in the inputRefs object.
   * For the 'trackingNo' field, the value is split by commas, trimmed, and filtered to remove empty entries.
   * For other fields, the value is set directly.
   *
   * @param {number} index - The index of the input field being changed.
   * @param {string} field - The name of the field being updated.
   * @param {string} value - The new value for the field.
   */
  const handleChange = (index, field, value) => {
    if (!inputRefs.current[index]) {
      inputRefs.current[index] = {};
    }
    inputRefs.current[index][field] =
      field === 'trackingNo'
        ? value
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean)
        : value;
  };

  const [rowToDelete, setRowToDelete] = useState(null);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  /**
   * Handles the delete button click event for a specific row.
   * Sets the ID of the row to be deleted and displays the delete confirmation alert.
   *
   * @param {string|number} id - The unique identifier of the row to delete.
   */
  const handleDeleteDelClick = (id) => {
    setRowToDelete(id);
    setShowDeleteAlert(true);
  };

  /**
   * @param {array} country
   * @returns name of the country based on its value
   */
  const getCountryName = (country) => countryOptions.find((o) => o.value === country).text;
  // Select options countries filter
  const selectOptions = countryOptions.map(({ text, ...rest }) => ({
    ...rest,
    label: text
  }));
  // Select options managers filter
  const selectOptionsManagers = managers.map(({ _id, fname, sname, ...rest }) => ({
    ...rest,
    value: _id,
    label: fname + ' ' + sname
  }));

  // Define columns
  const delColumns = [
    {
      text: 'ID',
      dataField: '_id',
      sort: true,
      editable: false,
      csvExport: false,
      formatter: (cell, row, rowIndex) => rowIndex + 1,
      headerClasses: 'id-header',
      footer: ''
    },
    {
      text: 'Shipment Date',
      dataField: 'sendDate',
      sort: true,
      csvExport: true,
      hidden: false,
      editable: true,
      editor: {
        type: Type.DATE
      },
      filter: dateFilter({
        withoutEmptyComparatorOption: true
      }),
      headerClasses: 'column-header',
      csvFormatter: (cell, row, rowIndex) => {
        return row.sendDate && !isNaN(new Date(row.sendDate.split(',')[0].split('/').reverse().join('-')))
          ? new Date(row.sendDate.split(',')[0].split('/').reverse().join('-')).toLocaleDateString('en-GB')
          : row.sendDate;
      },
      formatter: (cellContent, info) => (
        <h5 className='font-size-16 mb-1'>
          {info.sendDate && !isNaN(new Date(info.sendDate.split(',')[0].split('/').reverse().join('-')))
            ? new Date(info.sendDate.split(',')[0].split('/').reverse().join('-')).toLocaleDateString('en-GB')
            : info.sendDate}
        </h5>
      ),
      footer: ''
    },
    {
      text: 'Tracking Number',
      dataField: 'delInfo.trackingNo',
      sort: true,
      csvExport: true,
      hidden: false,
      editable: true,
      filter: textFilter(),
      headerClasses: 'column-header',
      formatter: (cellContent, info) => <TrackingColumnPrTest tableData={info} option='logistic' />,
      // formatter: (cellContent, info) => <></>,
      footer: ''
    },
    {
      text: 'Delivery Fee',
      dataField: 'delInfo.delFee',
      sort: true,
      csvExport: true,
      hidden: false,
      editable: true,
      headerClasses: 'column-header',
      csvFormatter: (cell, row, rowIndex) => {
        return row.delInfo?.delFee !== 0 ? row.delInfo?.delFee : '';
      },
      formatter: (cellContent, info) => <h5 className='font-size-16 mb-1 '>{info.delInfo?.delFee !== 0 && info.delInfo?.delFee}</h5>,
      footer: (columnData) => {
        const total = columnData
          .map(Number)
          .reduce((acc, num) => acc + num, 0)
          .toFixed(2);
        return (
          <div className='btn-success p-2 rounded-pill text-center'>
            <span className='font-size-16 fw-bold'>
              <NumberFormat value={total} displayType='text' thousandSeparator={true} prefix='₩' />
            </span>
          </div>
        );
      }
    },
    {
      text: '1C Order Number',
      dataField: 'invoiceNo',
      sort: true,
      csvExport: true,
      hidden: false,
      editable: false,
      headerClasses: 'problem-row column-header',
      classes: 'problem-row',
      filter: textFilter(),
      formatter: (cellContent, info) => <h5 className='font-size-16 mb-1 '>{info.invoiceNo}</h5>,
      footer: ''
    },
    {
      text: 'Buyer name',
      dataField: 'clientName',
      sort: true,
      csvExport: true,
      hidden: false,
      editable: false,
      headerClasses: 'problem-row column-header',
      classes: 'problem-row',
      filter: textFilter(),
      formatter: (cellContent, info) => <h5 className='font-size-16 mb-1 '>{info.clientName}</h5>,
      footer: ''
    },
    {
      text: 'Country',
      dataField: 'country',
      sort: true,
      csvExport: true,
      hidden: false,
      editable: true,
      headerClasses: 'column-header',
      filter: selectFilter({
        options: selectOptions
      }),
      csvFormatter: (cell, row, rowIndex) => `${row.country ? getCountryName(row.country) : ''}`,
      formatter: (cellContent, info) => <h5 className='font-size-16 mb-1 '>{info.country && getCountryName(info.country)}</h5>,
      editor: {
        type: Type.SELECT,
        options: countryOptions.map(({ value, text }) => ({
          value,
          label: text
        }))
      },
      footer: ''
    },
    {
      text: 'Payment Date',
      dataField: 'delInfo.delPaidDate',
      sort: true,
      csvExport: true,
      hidden: false,
      editable: true,
      headerClasses: 'column-header',
      editor: {
        type: Type.DATE
      },
      filter: dateFilter({
        withoutEmptyComparatorOption: true
      }),
      csvFormatter: (cell, row, rowIndex) => {
        return row.delInfo?.delPaidDate ? new Date(row.delInfo?.delPaidDate).toLocaleDateString('en-GB') : '';
      },
      formatter: (cellContent, info) => <h5 className='font-size-16 mb-1 '>{info.delInfo?.delPaidDate && new Date(info.delInfo?.delPaidDate).toLocaleDateString('en-GB')}</h5>,
      footer: ''
    },
    {
      text: 'Invoice No.',
      dataField: 'delInfo.delInvoiceNo',
      sort: true,
      csvExport: true,
      hidden: false,
      editable: true,
      headerClasses: 'column-header',
      filter: textFilter(),
      formatter: (cellContent, info) => <h5 className='font-size-16 mb-1 '>{info.delInfo?.delInvoiceNo}</h5>,
      footer: ''
    },
    {
      text: 'Comments',
      dataField: 'delInfo.comment',
      sort: true,
      csvExport: true,
      hidden: false,
      editable: true,
      headerClasses: 'column-header',
      filter: textFilter(),
      formatter: (cellContent, info) => <h5 className='font-size-16 mb-1 '>{info.delInfo?.comment}</h5>,
      footer: ''
    },
    {
      dataField: 'menu',
      isDummyField: true,
      csvExport: false,
      text: 'Action',
      editable: false,
      headerClasses: 'menu-header',
      formatter: (cellContent, info) => (
        <div className='d-flex gap-3'>
          <Link className='text-info' to='#'>
            <i
              className='mdi mdi-content-copy font-size-18'
              id='edittooltip'
              onClick={() => {
                setActiveTab((prev) => {
                  handleCopyOrderClick(info, info.delService, prev);
                  return prev;
                });
              }}
            ></i>
          </Link>
          {info.canBeDeleted && (
            <Link className='text-danger' to='#'>
              <i className='mdi mdi-delete font-size-18' id='deletetooltip' onClick={() => handleDeleteDelClick(info._id)}></i>
            </Link>
          )}
        </div>
      ),
      footer: ''
    }
  ];
  const logColumns = [
    {
      text: 'ID',
      dataField: '_id',
      sort: true,
      editable: false,
      csvExport: false,
      formatter: (cell, row, rowIndex) => rowIndex + 1
    },
    {
      text: 'Log Services',
      dataField: 'logInfo.logService',
      sort: true,
      csvExport: true,
      hidden: false,
      editable: true,
      formatter: (cellContent, info) => <h5 className='font-size-16 mb-1 '>{info.logInfo?.logService}</h5>
    },
    {
      text: 'Log Name',
      dataField: 'logInfo.logName',
      sort: true,
      csvExport: true,
      hidden: false,
      editable: true,
      filter: textFilter(),
      formatter: (cellContent, info) => <h5 className='font-size-16 mb-1 '>{info.logInfo?.logName}</h5>
    },
    {
      text: 'Documents',
      dataField: 'logInfo.logDocs',
      sort: true,
      csvExport: true,
      hidden: false,
      editable: true,
      formatter: (cellContent, info) => <h5 className='font-size-16 mb-1 '>{info.logInfo?.logDocs}</h5>
    },
    {
      text: 'Shipment Date',
      dataField: 'sendDate',
      sort: true,
      csvExport: true,
      hidden: false,
      editable: true,
      editor: {
        type: Type.DATE
      },
      filter: dateFilter({
        withoutEmptyComparatorOption: true
      }),
      csvFormatter: (cell, row, rowIndex) => {
        return row.sendDate && !isNaN(new Date(row.sendDate.split(',')[0].split('/').reverse().join('-')))
          ? new Date(row.sendDate.split(',')[0].split('/').reverse().join('-')).toLocaleDateString('en-GB')
          : row.sendDate;
      },
      formatter: (cellContent, info) => (
        <h5 className='font-size-16 mb-1'>
          {info.sendDate && !isNaN(new Date(info.sendDate.split(',')[0].split('/').reverse().join('-')))
            ? new Date(info.sendDate.split(',')[0].split('/').reverse().join('-')).toLocaleDateString('en-GB')
            : info.sendDate}
        </h5>
      )
    },
    {
      text: 'Departure Date',
      dataField: 'logInfo.depDate',
      sort: true,
      csvExport: true,
      hidden: false,
      editable: true,
      editor: {
        type: Type.DATE
      },
      filter: dateFilter({
        withoutEmptyComparatorOption: true
      }),
      csvFormatter: (cell, row, rowIndex) => {
        return row.logInfo?.depDate && !isNaN(new Date(row.logInfo?.depDate.split(',')[0].split('/').reverse().join('-')))
          ? new Date(row.logInfo?.depDate.split(',')[0].split('/').reverse().join('-')).toLocaleDateString('en-GB')
          : '';
      },
      formatter: (cellContent, info) => (
        <h5 className='font-size-16 mb-1 '>
          {info.logInfo?.depDate && !isNaN(new Date(info.logInfo?.depDate.split(',')[0].split('/').reverse().join('-')))
            ? new Date(info.logInfo?.depDate.split(',')[0].split('/').reverse().join('-')).toLocaleDateString('en-GB')
            : info.logInfo?.depDate}
        </h5>
      )
    },
    {
      text: 'Delivery Cost',
      dataField: 'logInfo.delCost',
      sort: true,
      csvExport: true,
      hidden: false,
      editable: true,
      filter: textFilter(),
      csvFormatter: (cell, row, rowIndex) => {
        return row.logInfo?.delCost ? row.logInfo?.delCost : '';
      },
      formatter: (cellContent, info) => (
        <h5 className='font-size-16 mb-1 '>
          {info.logInfo?.delCost !== 0 && <NumberFormat value={parseFloat(info.logInfo?.delCost).toFixed(0)} displayType='text' thousandSeparator={true} prefix='' />}
        </h5>
      )
    },
    {
      text: 'Delivery cost+VAT',
      dataField: 'logInfo.delCostVAT',
      sort: true,
      csvExport: true,
      hidden: false,
      editable: true,
      filter: textFilter(),
      csvFormatter: (cell, row, rowIndex) => {
        return row.logInfo?.delCostVAT ? row.logInfo?.delCostVAT : '';
      },
      formatter: (cellContent, info) => (
        <h5 className='font-size-16 mb-1 '>
          {info.logInfo?.delCostVAT !== 0 && <NumberFormat value={parseFloat(info.logInfo?.delCostVAT).toFixed(0)} displayType='text' thousandSeparator={true} prefix='' />}
        </h5>
      )
    },
    {
      text: 'Delivery',
      dataField: 'logInfo.delivery',
      sort: true,
      csvExport: true,
      hidden: false,
      editable: true,
      editor: {
        type: Type.SELECT,
        options: [
          {
            value: 'Expense',
            label: 'Expense'
          },
          {
            value: 'Buyer',
            label: 'Buyer'
          }
        ]
      },
      filter: textFilter(),
      formatter: (cellContent, info) => <h5 className='font-size-16 mb-1 '>{info.logInfo?.delivery}</h5>
    },
    {
      text: '1C Order Number',
      dataField: 'invoiceNo',
      sort: true,
      csvExport: true,
      hidden: false,
      editable: false,
      headerClasses: 'problem-row',
      classes: 'problem-row',
      filter: textFilter(),
      csvFormatter: (cell, row, rowIndex) => `${row.clientName} ${row.invoiceNo}`,
      formatter: (cellContent, info) => (
        <h5 className='font-size-16 mb-1 '>
          {info.clientName} {info.invoiceNo}
        </h5>
      )
    },
    {
      text: 'Country',
      dataField: 'country',
      sort: true,
      csvExport: true,
      hidden: false,
      editable: true,
      // headerClasses: 'problem-row',
      // classes: 'problem-row',
      // filter: textFilter(),
      filter: selectFilter({
        options: selectOptions
      }),
      csvFormatter: (cell, row, rowIndex) => `${row.country ? getCountryName(row.country) : ''}`,
      formatter: (cellContent, info) => <h5 className='font-size-16 mb-1 '>{info.country && getCountryName(info.country)}</h5>,
      editor: {
        type: Type.SELECT,
        options: countryOptions.map(({ value, text }) => ({
          value,
          label: text
        }))
      }
    },
    {
      text: 'Manager',
      dataField: 'manager._id',
      sort: true,
      csvExport: true,
      hidden: false,
      editable: false,
      headerClasses: 'problem-row',
      classes: 'problem-row',
      filter: selectFilter({
        options: selectOptionsManagers
      }),
      csvFormatter: (cell, row, rowIndex) => (row.manager?._id ? row.manager.fname + ' ' + row.manager.sname : ''),
      formatter: (cellContent, info) => (
        <h5 className='font-size-16 mb-1'>
          {info.manager?.fname
            ? `${info.manager.fname} ${info.manager.sname}`
            : (() => {
                const manager = managers.find((manager) => manager._id === info.manager?._id);
                return manager ? `${manager.fname} ${manager.sname}` : '';
              })()}
        </h5>
      ),
      editor: {
        type: Type.SELECT,
        options: selectOptionsManagers.map(({ value, label }) => ({
          value,
          label
        }))
      }
    },
    {
      text: 'comment',
      dataField: 'logInfo.comment',
      sort: true,
      csvExport: true,
      hidden: false,
      editable: true,
      filter: textFilter(),
      formatter: (cellContent, info) => <h5 className='font-size-16 mb-1 '>{info.logInfo?.comment}</h5>
    },
    {
      text: 'Paid on',
      dataField: 'logInfo.delPaidDate',
      sort: true,
      csvExport: true,
      hidden: false,
      editable: true,
      editor: {
        type: Type.DATE
      },
      filter: dateFilter({
        withoutEmptyComparatorOption: true
      }),
      csvFormatter: (cell, row, rowIndex) => {
        return row.logInfo?.delPaidDate && !isNaN(new Date(row.logInfo?.delPaidDate.split(',')[0].split('/').reverse().join('-')))
          ? new Date(row.logInfo?.delPaidDate.split(',')[0].split('/').reverse().join('-')).toLocaleDateString('en-GB')
          : '';
      },
      formatter: (cellContent, info) => (
        <h5 className='font-size-16 mb-1 '>
          {info.logInfo?.delPaidDate && !isNaN(new Date(info.logInfo?.delPaidDate.split(',')[0].split('/').reverse().join('-')))
            ? new Date(info.logInfo?.delPaidDate.split(',')[0].split('/').reverse().join('-')).toLocaleDateString('en-GB')
            : info.logInfo?.delPaidDate}
          {/* {info.logInfo?.delPaidDate} */}
        </h5>
      )
    },
    {
      text: 'Logistics Cost',
      dataField: 'logInfo.logCost',
      sort: true,
      csvExport: true,
      hidden: false,
      editable: true,
      // filter: textFilter(),
      csvFormatter: (cell, row, rowIndex) => {
        return row.logInfo?.logCost ? row.logInfo?.logCost : '';
      },
      formatter: (cellContent, info) => (
        <h5 className='font-size-16 mb-1 '>
          {info.logInfo?.logCost !== 0 && <NumberFormat value={parseFloat(info.logInfo?.logCost).toFixed(2)} displayType='text' thousandSeparator={true} prefix='' />}
        </h5>
      )
    },
    {
      text: 'Currency',
      dataField: 'logInfo.logCostCurrency',
      sort: true,
      csvExport: true,
      hidden: false,
      editable: true,
      editor: {
        type: Type.SELECT,
        options: [
          {
            value: 'KRW',
            label: 'KRW'
          },
          {
            value: 'USD',
            label: 'USD'
          },
          {
            value: 'EUR',
            label: 'EUR'
          }
        ]
      },
      // filter: textFilter(),
      formatter: (cellContent, info) => <h5 className='font-size-16 mb-1 '>{info.logInfo?.logCostCurrency}</h5>
    },
    {
      dataField: 'menu',
      isDummyField: true,
      csvExport: false,
      text: 'Action',
      editable: false,
      formatter: (cellContent, info) => (
        <div className='d-flex gap-3'>
          <Link className='text-info' to='#'>
            <i className='mdi mdi-content-copy font-size-18' id='edittooltip' onClick={() => handleCopyOrderClick(info, 'Others', activeTab)}></i>
          </Link>
          {info.canBeDeleted && (
            <Link className='text-danger' to='#'>
              <i className='mdi mdi-delete font-size-18' id='deletetooltip' onClick={() => handleDeleteDelClick(info._id)}></i>
            </Link>
          )}
        </div>
      )
    }
  ];
  const tabConfigs = {
    1: {
      columns: delColumns,
      defaultSorted: [{ dataField: 'sendDate', order: 'asc' }]
    },
    2: {
      columns: delColumns,
      defaultSorted: [{ dataField: 'sendDate', order: 'asc' }]
    },
    3: {
      columns: delColumns,
      defaultSorted: [{ dataField: 'sendDate', order: 'asc' }]
    },
    4: {
      columns: delColumns,
      defaultSorted: [{ dataField: 'sendDate', order: 'asc' }]
    },
    5: {
      columns: delColumns,
      defaultSorted: [{ dataField: 'sendDate', order: 'asc' }]
    },
    6: {
      columns: logColumns,
      defaultSorted: [{ dataField: 'sendDate', order: 'asc' }]
    }
  };
  const tabs = [
    { id: 1, label: 'DHL', icon: 'mdi-calendar' },
    { id: 2, label: 'FedEx', icon: 'mdi-calendar-multiple-check' },
    { id: 3, label: 'EMS', icon: 'mdi-account-multiple' },
    { id: 4, label: 'EMS MC', icon: 'mdi-account-multiple' },
    { id: 5, label: 'EMS MN', icon: 'mdi-account-multiple' },
    { id: 6, label: 'Others', icon: 'mdi-account-multiple' }
  ];
  // Years options
  const currentYear = new Date().getFullYear();
  const yearsOptions = Array.from({ length: 3 }, (_, i) => currentYear - (2 - i)).reduce((acc, year) => {
    acc.push({ key: String(year), icon: '', label: String(year) });
    return acc;
  }, []);
  // Months options
  const rawMonths = [
    { icon: Snowflake, name: 'January' },
    { icon: Snowflake, name: 'February' },
    { icon: Flower2, name: 'March' },
    { icon: Flower2, name: 'April' },
    { icon: Flower2, name: 'May' },
    { icon: Sun, name: 'June' },
    { icon: Sun, name: 'July' },
    { icon: Sun, name: 'August' },
    { icon: Leaf, name: 'September' },
    { icon: Leaf, name: 'October' },
    { icon: Leaf, name: 'November' },
    { icon: Snowflake, name: 'December' }
  ];
  const monthsOptions = [
    ...rawMonths.map((item, index) => ({
      key: index.toString(),
      icon: <item.icon size={22} className='ms-3 pe-2' />,
      label: item.name
    })),
    {
      key: 12,
      icon: <Atom size={22} className='ms-3 pe-2' />,
      label: 'All'
    }
  ];
  const currentDate = new Date();
  const currentMonthIndex = currentDate.getMonth(); // 0-11
  const currentMonthKey = Object.keys(monthsOptions)[currentMonthIndex]; // Get corresponding key
  const initialSelected = { years: [String(currentYear)], months: [currentMonthKey] }
  const cardRef = useRef(null);
  const [cardHeight, setCardHeight] = useState(0);
  useEffect(() => {
    if (!cardRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const newHeight = entry.contentRect.height;
        if (newHeight > 0) setCardHeight(newHeight);
      }
    });
    resizeObserver.observe(cardRef.current);
    return () => resizeObserver.disconnect();
  }, [activeTab]);


  // const formatToInputDate = (dateStr) => {
  //   if (!dateStr) return ''
  //   const [year, month, day] = dateStr.split('-')
  //   return `${month}/${day}/${year}`
  // }
  /**
   * Renders custom filter form fields for the logistics page.
   *
   * This component provides three input fields for filtering data:
   * - Start Date: A date picker for selecting the start date.
   * - End Date: A date picker for selecting the end date.
   * - Client Name: A text input for entering the client's name.
   *
   * The input values are controlled by the `filterFormValues` state,
   * and updates are handled via the `setFilterFormValues` function.
   *
   * @constant
   * @type {JSX.Element}
   */
  const formatDateForInput = (date) => date.toISOString().split('T')[0];
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterFormValues((prev) => ({
      ...prev,
      [name]: name === 'startDate' || name === 'endDate' ? formatDateForInput(new Date(value)) : value
    }));
  };
  const filterCustomContent = (
    <>
      <FormGroup className='pe-1'>
        <AvField type='date' name='startDate' className='form-control prfilter fw-bold fs-5' value={filterFormValues?.startDate} onChange={handleFilterChange}></AvField>
      </FormGroup>
      <FormGroup className='pe-1'>
        <AvField type='date' name='endDate' className='form-control prfilter fw-bold fs-5' value={filterFormValues?.endDate} onChange={handleFilterChange}></AvField>
      </FormGroup>
      <FormGroup className='pe-1'>
        <AvField
          type='text'
          name='clientName'
          className='form-control prfilter fw-bold fs-5'
          value={filterFormValues?.clientName}
          onChange={(e) => setFilterFormValues({ ...filterFormValues, clientName: e.target.value })}
        ></AvField>
      </FormGroup>
      <FormGroup className='me-1'>
        <button className='btn btn-success save-user'>
          Filter
        </button>
      </FormGroup>
    </>
  );
console.log(filterFormValues?.startDate)
  /**
   * Renders an AvField component configured as a date input for selecting a date.
   * This component is styled with Bootstrap classes for margin, font weight, and font size.
   * Intended to be used as extra content in global actions, such as filters or forms.
   */
  const globalActionExtraContent = (
    <AvField
      type='date'
      name='date'
      className='form-control ms-2 me-2 fw-bold fs-5'
    ></AvField>
  );

  return (
    <React.Fragment>
      <PageLoader progress={progress} step={step} visible={visible} />
      <div className='page-content'>
        <MetaTags>
          <title>Logistic Fees</title>
        </MetaTags>
        <Container fluid>
          <Breadcrumbs title='Korupharma' breadcrumbItem='Logistic Fees' />
          <Row className='card-section'>
            <Col lg='12'>
              <div className='pcss3t logtb pcss3t-effect-scale pcss3t-theme-1'>
                {tabs.map((tab) => (
                  <React.Fragment key={tab.id}>
                    <input type='radio' name='pcss3t' id={`tab${tab.id}`} checked={activeTab === tab.id} onChange={() => setActiveTab(tab.id)} className={`tab-content-${tab.id}`} />
                    <label htmlFor={`tab${tab.id}`}>
                      <i className={`mdi ${tab.icon}`}></i> {tab.label}
                    </label>
                  </React.Fragment>
                ))}
                <BtnModal
                  btnIcon='mdi-plus-circle-outline'
                  modalSize='xl'
                  btnLabel='Add New Delivery'
                  modalTitle={isCopy ? 'Copy Selected Order' : 'Add New Delivery'}
                  modalBtnLabel='Add Expense'
                  modalOpen={modalAdd}
                  toggleModal={toggleAddModal}
                  handleNewClick={handleNewDeliveryClick}
                  handleSubmit={handleNewDeliverySubmit}
                >
                  <Row className='mb-3'>
                    <Col md={10}>
                      <AvField
                        type='select'
                        name='delServ'
                        id='delServ'
                        className='form-select text-center'
                        label='Delivery Type'
                        errorMessage='Please Select Delivery Type'
                        validate={{
                          required: {
                            value: true
                          }
                        }}
                        onChange={(e) => setDeliveryService(e.target.value)}
                        value={delServ}
                      >
                        <option>Choose</option>
                        <option>DHL</option>
                        <option>FedEx</option>
                        <option>EMS</option>
                        <option>EMS MC</option>
                        <option>EMS MN</option>
                        <option>Others</option>
                      </AvField>
                    </Col>
                  </Row>
                  {delServ &&
                    (delServ === 'Others' ? (
                      <Row>
                        {addDelsFields.map((field, index) => (
                          <React.Fragment key={index}>
                            <Col md={10}>
                              <Row>
                                <Col md={3}>
                                  <FormGroup className='mb-3'>
                                    <AvField
                                      name={`logService${index}`}
                                      label='Log Services '
                                      type='text'
                                      autoComplete='off'
                                      list='logservices'
                                      value={field.logService || ''}
                                      validate={{
                                        required: {
                                          value: true
                                        }
                                      }}
                                      onChange={(e) => handleChange(index, 'logService', e.target.value)}
                                      onBlur={() => handleBlur(index, 'logService')}
                                    />
                                    <datalist id='logservices'>
                                      <optgroup label='Log Services'>
                                        <option>KP</option>
                                        <option>Log</option>
                                        <option>공유</option>
                                        <option>올바로</option>
                                      </optgroup>
                                    </datalist>
                                  </FormGroup>
                                </Col>
                                <Col md={3}>
                                  <FormGroup className='mb-3'>
                                    <AvField
                                      name={`logName${index}`}
                                      label='Log Name'
                                      type='text'
                                      autoComplete='off'
                                      value={field.logName || ''}
                                      onChange={(e) => handleChange(index, 'logName', e.target.value)}
                                      onBlur={() => handleBlur(index, 'logName')}
                                    />
                                  </FormGroup>
                                </Col>
                                <Col md={3}>
                                  <FormGroup className='mb-3'>
                                    <AvField
                                      name={`logDocs${index}`}
                                      label='Documents'
                                      type='text'
                                      autoComplete='off'
                                      list='documents'
                                      value={field.logDocs || ''}
                                      onChange={(e) => handleChange(index, 'logDocs', e.target.value)}
                                      onBlur={() => handleBlur(index, 'logDocs')}
                                    />
                                    <datalist id='documents'>
                                      <optgroup label='Docs'>
                                        <option>Export Permit</option>
                                        <option>AWB</option>
                                        <option>물품인수증</option>
                                        <option>PR</option>
                                      </optgroup>
                                    </datalist>
                                  </FormGroup>
                                </Col>
                                <Col md={3}>
                                  <FormGroup className='mb-3'>
                                    <Label>Shipment Date</Label>
                                    <InputMask
                                      mask='99/99/9999'
                                      value={field.sendDate}
                                      name={`sendDate${index}`}
                                      // onChange={onChange}
                                      onChange={(e) => handleInputChange(index, 'sendDate', e.target.value)}
                                      className={correctShipDate ? ' form-control input-mask' : 'form-control input-mask is-invalid'}
                                    />
                                    {!correctShipDate && <div className='invalid-feedback'>Invalid Date. Please use dd/mm/yyyy format</div>}
                                  </FormGroup>
                                </Col>
                              </Row>
                              <Row>
                                <Col md={3}>
                                  <FormGroup className='mb-3'>
                                    <AvField
                                      name={`delCost${index}`}
                                      label='Delivery Cost'
                                      type='number'
                                      autoComplete='off'
                                      value={field.delCost || ''}
                                      onChange={(e) => handleChange(index, 'delCost', e.target.value)}
                                      onBlur={() => handleBlur(index, 'delCost')}
                                    />
                                  </FormGroup>
                                </Col>
                                <Col md={3}>
                                  <FormGroup className='mb-3'>
                                    <AvField
                                      name={`delCostVAT${index}`}
                                      label='Delivery Cost+VAT'
                                      type='number'
                                      autoComplete='off'
                                      value={field.delCostVAT || ''}
                                      onChange={(e) => handleChange(index, 'delCostVAT', e.target.value)}
                                      onBlur={() => handleBlur(index, 'delCostVAT')}
                                    />
                                  </FormGroup>
                                </Col>
                                <Col md={3}>
                                  <FormGroup className='mb-3'>
                                    <AvField
                                      type='select'
                                      name='delivery'
                                      id='delivery'
                                      className='form-select'
                                      label='Delivery'
                                      errorMessage='Please select delivery'
                                      onChange={(e) => handleChange(index, 'delivery', e.target.value)}
                                      onBlur={() => handleBlur(index, 'delivery')}
                                      // onChange={this.onChangeSelectManager}
                                      value={field.delivery}
                                    >
                                      <option>Choose</option>
                                      <option value='Expense'>Expense</option>
                                      <option value='Buyer'>Buyer</option>
                                    </AvField>
                                  </FormGroup>
                                </Col>
                                <Col md={3}>
                                  <FormGroup className='mb-3'>
                                    <Label>Departure Date</Label>
                                    <InputMask
                                      mask='99/99/9999'
                                      value={field.depDate}
                                      name={`depDate${index}`}
                                      // onChange={onChange}
                                      onChange={(e) => handleInputChange(index, 'depDate', e.target.value)}
                                      className={correctDepDate ? ' form-control input-mask' : 'form-control input-mask is-invalid'}
                                    />
                                    {!correctDepDate && <div className='invalid-feedback'>Invalid Date. Please use dd/mm/yyyy format</div>}
                                  </FormGroup>
                                </Col>
                              </Row>
                              <Row>
                                <Col md={3}>
                                  <FormGroup className='mb-3'>
                                    <AvField
                                      name={`oneCOrder${index}`}
                                      label='1C Order No'
                                      type='text'
                                      autoComplete='off'
                                      disabled={delivery !== ''}
                                      value={field.oneCOrder || ''}
                                      validate={{
                                        required: {
                                          value: true
                                        }
                                      }}
                                      onChange={(e) => handleChange(index, 'oneCOrder', e.target.value)}
                                      onBlur={() => handleBlur(index, 'oneCOrder')}
                                    />
                                  </FormGroup>
                                </Col>
                                <Col md={3}>
                                  <FormGroup className='mb-3'>
                                    <AvField
                                      name={`clientName${index}`}
                                      label='Client Name'
                                      type='text'
                                      autoComplete='off'
                                      disabled={delivery !== ''}
                                      value={field.clientName || ''}
                                      onChange={(e) => handleChange(index, 'clientName', e.target.value)}
                                      onBlur={() => handleBlur(index, 'clientName')}
                                    />
                                  </FormGroup>
                                </Col>
                                <Col md={3}>
                                  <FormGroup className='mb-3'>
                                    <Label htmlFor={`country${index}`}>Country</Label>
                                    <Dropdown
                                      placeholder='Select Country'
                                      fluid
                                      search
                                      selection
                                      options={countryOptions}
                                      name={`country${index}`}
                                      disabled={delivery !== ''}
                                      defaultValue={field.country}
                                      onChange={(e) => handleInputChange(index, 'country', e.target.value)}
                                      id={`country${index}`}
                                    />
                                  </FormGroup>
                                </Col>
                                <Col md={3}>
                                  <FormGroup className='mb-3'>
                                    <AvField
                                      type='select'
                                      name='manager'
                                      id='manager'
                                      className='form-select'
                                      label='Manager'
                                      errorMessage='Please select manager'
                                      disabled={delivery !== ''}
                                      onChange={(e) => handleChange(index, 'manager', e.target.value)}
                                      onBlur={() => handleBlur(index, 'manager')}
                                      value={field.manager}
                                    >
                                      <option>Choose</option>
                                      {managers.map((manager) => {
                                        return (
                                          <option key={manager._id} value={manager._id}>
                                            {manager.fname} {manager.sname}
                                          </option>
                                        );
                                      })}
                                    </AvField>
                                  </FormGroup>
                                </Col>
                              </Row>
                              <Row>
                                <Col md={3}>
                                  <FormGroup className='mb-3'>
                                    <AvField
                                      name={`comment${index}`}
                                      label='Comments'
                                      type='text'
                                      autoComplete='off'
                                      value={field.comment || ''}
                                      onChange={(e) => handleChange(index, 'comment', e.target.value)}
                                      onBlur={() => handleBlur(index, 'comment')}
                                    />
                                  </FormGroup>
                                </Col>
                                <Col md={3}>
                                  <FormGroup className='mb-3'>
                                    <Label>Delivery Payment Date</Label>
                                    <InputMask
                                      mask='99/99/9999'
                                      value={field.delPaidDate}
                                      name={`delPaidDate${index}`}
                                      // onChange={onChange}
                                      onChange={(e) => handleInputChange(index, 'delPaidDate', e.target.value)}
                                      className={correctDelPaidDate ? ' form-control input-mask' : 'form-control input-mask is-invalid'}
                                    />
                                    {!correctDelPaidDate && <div className='invalid-feedback'>Invalid Date. Please use dd/mm/yyyy format</div>}
                                  </FormGroup>
                                </Col>
                                <Col md={3}>
                                  <FormGroup className='mb-3'>
                                    <AvField
                                      name={`logCost${index}`}
                                      label='Logistics Cost'
                                      type='number'
                                      autoComplete='off'
                                      value={field.logCost || ''}
                                      onChange={(e) => handleChange(index, 'logCost', e.target.value)}
                                      onBlur={() => handleBlur(index, 'logCost')}
                                    />
                                  </FormGroup>
                                </Col>
                                <Col md={3}>
                                  <FormGroup className='mb-3'>
                                    <AvField
                                      type='select'
                                      name='logCostCurrency'
                                      id='logCostCurrency'
                                      className='form-select'
                                      label='Currency'
                                      errorMessage='Please select currency'
                                      onChange={(e) => handleChange(index, 'logCostCurrency', e.target.value)}
                                      onBlur={() => handleBlur(index, 'logCostCurrency')}
                                      value={field.logCostCurrency}
                                    >
                                      <option>Choose</option>
                                      <option value='KRW'>KRW</option>
                                      <option value='USD'>USD</option>
                                      <option value='EUR'>EUR</option>
                                    </AvField>
                                  </FormGroup>
                                </Col>
                              </Row>
                            </Col>
                            {index === addDelsFields.length - 1 && (
                              <Col md={1} className='d-flex align-items-center mt-2'>
                                <Link className='text-success' to='#'>
                                  <i className='mdi mdi-plus font-size-24' onClick={addInputField}></i>
                                </Link>
                              </Col>
                            )}
                            {index !== 0 && (
                              <Col md={1} className='d-flex align-items-center mt-2'>
                                <Link className='text-danger' to='#'>
                                  <i className='mdi mdi-delete font-size-24' id='deletetooltip' onClick={() => removeInputField(index)}></i>
                                </Link>
                              </Col>
                            )}
                            <hr className='icon' />
                          </React.Fragment>
                        ))}
                      </Row>
                    ) : (
                      <Row>
                        {addDelsFields.map((field, index) => (
                          <React.Fragment key={index}>
                            <Col md={10}>
                              <Row>
                                <Col md={4}>
                                  <FormGroup className='mb-3'>
                                    <Label>Shipment Date</Label>
                                    <InputMask
                                      mask='99/99/9999'
                                      value={field.sendDate}
                                      name={`sendDate${index}`}
                                      // onChange={onChange}
                                      onChange={(e) => handleInputChange(index, 'sendDate', e.target.value)}
                                      className={correctShipDate ? ' form-control input-mask' : 'form-control input-mask is-invalid'}
                                    />
                                    {!correctShipDate && <div className='invalid-feedback'>Invalid Date. Please use dd/mm/yyyy format</div>}
                                  </FormGroup>
                                </Col>
                                <Col md={4}>
                                  <FormGroup className='mb-3'>
                                    <AvField
                                      name={`trackingNo${index}`}
                                      label='Tracking No'
                                      type='text'
                                      autoComplete='off'
                                      value={field.trackingNo?.join(', ') || ''}
                                      onChange={(e) => handleChange(index, 'trackingNo', e.target.value)}
                                      onBlur={() => handleBlur(index, 'trackingNo')}
                                    />
                                  </FormGroup>
                                </Col>
                                <Col md={4}>
                                  <FormGroup className='mb-3'>
                                    <AvField
                                      name={`comment${index}`}
                                      label='Comments'
                                      type='text'
                                      autoComplete='off'
                                      value={field.comment || ''}
                                      onChange={(e) => handleChange(index, 'comment', e.target.value)}
                                      onBlur={() => handleBlur(index, 'comment')}
                                    />
                                  </FormGroup>
                                </Col>
                              </Row>
                              <Row>
                                <Col md={4}>
                                  <FormGroup className='mb-3'>
                                    <AvField
                                      name={`oneCOrder${index}`}
                                      label='1C Order No'
                                      type='text'
                                      autoComplete='off'
                                      disabled={delivery !== ''}
                                      value={field.oneCOrder || ''}
                                      onChange={(e) => handleChange(index, 'oneCOrder', e.target.value)}
                                      onBlur={() => handleBlur(index, 'oneCOrder')}
                                    />
                                  </FormGroup>
                                </Col>
                                <Col md={4}>
                                  <FormGroup className='mb-3'>
                                    <AvField
                                      name={`clientName${index}`}
                                      label='Client Name'
                                      type='text'
                                      autoComplete='off'
                                      disabled={delivery !== ''}
                                      value={field.clientName || ''}
                                      onChange={(e) => handleChange(index, 'clientName', e.target.value)}
                                      onBlur={() => handleBlur(index, 'clientName')}
                                    />
                                  </FormGroup>
                                </Col>
                                <Col md={4}>
                                  <FormGroup className='mb-3'>
                                    <Label htmlFor={`country${index}`}>Country</Label>
                                    <Dropdown
                                      placeholder='Select Country'
                                      fluid
                                      search
                                      selection
                                      options={countryOptions}
                                      name={`country${index}`}
                                      disabled={delivery !== ''}
                                      defaultValue={field.country}
                                      onChange={(e, data) => handleInputChange(index, 'country', data.value)}
                                      id={`country${index}`}
                                    />
                                  </FormGroup>
                                </Col>
                              </Row>
                              <Row>
                                <Col md={4}>
                                  <FormGroup className='mb-3'>
                                    <Label>Delivery Payment Date</Label>
                                    <InputMask
                                      mask='99/99/9999'
                                      value={field.delPaidDate}
                                      name={`delPaidDate${index}`}
                                      // onChange={onChange}
                                      onChange={(e) => handleInputChange(index, 'delPaidDate', e.target.value)}
                                      className={correctDelPaidDate ? ' form-control input-mask' : 'form-control input-mask is-invalid'}
                                    />
                                    {!correctDelPaidDate && <div className='invalid-feedback'>Invalid Date. Please use dd/mm/yyyy format</div>}
                                  </FormGroup>
                                </Col>
                                <Col md={4}>
                                  <FormGroup className='mb-3'>
                                    <AvField
                                      name={`delInvoiceNo${index}`}
                                      label='Delivery Invoice No'
                                      type='text'
                                      autoComplete='off'
                                      value={field.delInvoiceNo || ''}
                                      onChange={(e) => handleChange(index, 'delInvoiceNo', e.target.value)}
                                      onBlur={() => handleBlur(index, 'delInvoiceNo')}
                                    />
                                  </FormGroup>
                                </Col>
                                <Col md={4}>
                                  <FormGroup className='mb-3'>
                                    <AvField
                                      name={`delFee${index}`}
                                      label='Delivery Fee'
                                      type='number'
                                      autoComplete='off'
                                      value={field.delFee || ''}
                                      onChange={(e) => handleChange(index, 'delFee', e.target.value)}
                                      onBlur={() => handleBlur(index, 'delFee')}
                                    />
                                  </FormGroup>
                                </Col>
                              </Row>
                            </Col>
                            {index === addDelsFields.length - 1 && (
                              <Col md={1} className='d-flex align-items-center mt-2'>
                                <Link className='text-success' to='#'>
                                  <i className='mdi mdi-plus font-size-24' onClick={addInputField}></i>
                                </Link>
                              </Col>
                            )}
                            {index !== 0 && (
                              <Col md={1} className='d-flex align-items-center mt-2'>
                                <Link className='text-danger' to='#'>
                                  <i className='mdi mdi-delete font-size-24' id='deletetooltip' onClick={() => removeInputField(index)}></i>
                                </Link>
                              </Col>
                            )}
                            <hr className='icon' />
                          </React.Fragment>
                        ))}
                      </Row>
                    ))}
                </BtnModal>
                <ul>
                  {tabs.map((tab) => {
                    if (!visitedTabs.has(tab.id)) return null;
                    const { columns, defaultSorted } = tabConfigs[tab.id] || {};
                    const isActive = tab.id === activeTab;
                    const currentTabData = tabDataMap[tab.id] || [];
                    return (
                      <AnimatePresence key={tab.id} exitBeforeEnter>
                        <motion.li
                          className={`tab-content tab-content-${tab.id} typography`}
                          initial={{ opacity: 0, x: 30 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -30 }}
                          transition={{ duration: 0.3 }}
                          style={{ display: isActive ? 'block' : 'none' }}
                        >
                          <div id='card-section' ref={cardRef}>
                            <Row className='card-section'>
                              <Col lg='12'>
                                <Card>
                                  <CardBody className='cardBodySticky'>
                                    {/* {isActive && !loading && columns && ( */}
                                      <ToolkitProvider
                                        keyField='_id'
                                        columns={columns}
                                        data={currentTabData}
                                        search
                                        exportCSV={{
                                          onlyExportFiltered: true,
                                          exportAll: false,
                                          fileName: `${tab.label}.csv`
                                        }}
                                      >
                                        {(props) => (
                                          <React.Fragment>
                                            <MainContent
											                        // overall info
                                              page={{ 
                                                pg: 'fees', 
                                                props: props,
                                                cardHeight, 
                                                showSearch: true, 
                                                showCSV: true, 
                                                showInTblFltr: false, 
                                                showDbFilter: true, 
                                                showBtn: false, 
                                                columns: props.baseProps.columns,
                                                data: props.baseProps.data,
                                                defaultSorted,
                                                filter: filterFactory(),
                                                cellEdit: cellEditFactory({
                                                  mode: 'click',
                                                  blurToSave: true,
                                                  afterSaveCell: (oldVal, newVal, row, column, rowId) => handleSaveDelInfo(oldVal, newVal, row, column, rowId, tab.id, columns)
                                                })
                                              }}
                                              filterOptions={{ years: yearsOptions, months: monthsOptions, }}
                                              filterDbOptions={{ filterFormValues, initialSelected, filterCustomContent, setFilterFormValues, filterFormSubmit, filterFormReset }}
                                              // tabs options
                                              tabsOptions={{ activeTab, filterValues, setFilterValues }}
                                              // table data opotions
                                              tableOptions={{ 
                                                includeSelect: true, 
                                                calculateFooter: true, 
                                                manualColumnWidthsIndex: activeTab === 6 ? [9, 11] : [2], 
                                                manualColumnWidths: activeTab === 6 ? [370, 250] : [370]
                                              }}
                                              // table actions 
                                              tableActions={{      
                                                handleSaveInfo: handleSaveDelInfo,  
                                                globalActionOptions:[
                                                  { value: 'sendDate', text: 'Set Shipment Date' },
                                                  { value: 'delPaidDate', text: 'Set Payment Date' }
                                                ], 
                                                globalActionBtnLbl: 'APPLY',
                                                globalActionExtraContent,
                                                handleGlobalActionSubmit 
                                              }}
                                            />
                                          </React.Fragment>
                                        )}
                                      </ToolkitProvider>
                                    {/* )} */}
                                  </CardBody>
                                </Card>
                              </Col>
                            </Row>
                          </div>
                        </motion.li>
                      </AnimatePresence>
                    );
                  })}
                </ul>
                <SweetAlert
                  show={showDeleteAlert}
                  danger
                  showCancel
                  confirmBtnText='Yes, delete it!'
                  confirmBtnBsStyle='danger'
                  title='Are you sure?'
                  onConfirm={handleDeleteDelivery}
                  onCancel={() => setShowDeleteAlert(false)}
                  focusCancelBtn
                >
                  The delivery will be deleted from the system!
                </SweetAlert>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}
